import React, { FC, ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import { BaggageGlyph, CheckCircleGlyph, GLYPH_COLORS } from '../primitive-glyphs/glyphs'
import { Text } from '../text'
import type { TextTone } from '../text/variants'

export type FlightTableCellContentType =
    | 'text'
    | 'link'
    | 'time'
    | 'baggage'
    | 'naflt'
    | 'badge'
    | 'tooltip'

/** Status fills available to the `badge` content (Figma `badge*` variants). */
export type FlightTableBadgeStatus = 'critical' | 'warning' | 'success' | 'info' | 'neutral'

const NAFLT_SECURITY_FILL: Record<string, string> = {
    Normal: 'bg-graphics-grey',
    Border: 'bg-graphics-blue',
    BorderSecurity: 'bg-graphics-red',
}

/**
 * Nature-of-flight badge fill by the flight's security level (Figma `naflt`,
 * node 7721:128857) — tenant-agnostic graphics tokens, shared by the web cell
 * and the mobile badge so the two platforms can't drift apart.
 *
 * Falls back to grey for anything outside the three levels the design defines:
 * the site config types `securityColors` as `Record<string, string>`, so
 * `getFlightSecurityLevel` is just `string` and a tenant config carrying a
 * fourth level reaches here without TypeScript flagging it.
 */
export const nafltSecurityFill = (securityLevel: string): string =>
    NAFLT_SECURITY_FILL[securityLevel] ?? 'bg-graphics-grey'

type BaseProps = {
    className?: string
    testID?: string
}

type TextContent = BaseProps & {
    type: 'text'
    /** Plain cell value, e.g. a flight number `AY 100`. P1 regular. */
    value: ReactNode
    tone?: TextTone
}

type LinkContent = BaseProps & {
    type: 'link'
    /** Clickable value rendered in the `content/link` blue (e.g. towing stand). */
    value: ReactNode
    /** Show a trailing check glyph (towing "done" marker). */
    checked?: boolean
}

type TimeContent = BaseProps & {
    type: 'time'
    /** Primary time value, e.g. `05:35`. */
    time: ReactNode
    /** Optional secondary date line below, e.g. `25.09`. */
    date?: ReactNode
    /** Tone of the date line — `warning` (yellow) flags estimated/off-day. */
    dateTone?: Extract<TextTone, 'warning' | 'muted' | 'default'>
}

type BaggageContent = BaseProps & {
    type: 'baggage'
    /** Delivery time, e.g. `0:12`. Defaults to the teal "in progress" tint. */
    time?: ReactNode
    /** Override the time colour class (delivery state). */
    timeClassName?: string
    /** Suitcase glyph colour (hex). */
    iconColor?: string
}

type NafltContent = BaseProps & {
    type: 'naflt'
    /** Two-letter nature-of-flight code, e.g. `SS`. */
    label: string
    /** Badge background class — defaults to neutral grey; pass a nature colour. */
    colorClassName?: string
}

type BadgeContent = BaseProps & {
    type: 'badge'
    /** Badge label, e.g. `Landed`. Truncates to one line when the column is narrow. */
    label: ReactNode
    /** Status fill — drives both the background and the ink. */
    status: FlightTableBadgeStatus
}

type TooltipContent = BaseProps & {
    type: 'tooltip'
    /** Tooltip body — usually a stack of P3 lines. */
    children: ReactNode
}

export type FlightTableCellContentProps =
    | TextContent
    | LinkContent
    | TimeContent
    | BaggageContent
    | NafltContent
    | BadgeContent
    | TooltipContent

// Figma `badge*` variants: bright status fill with the inverted (dark) ink —
// except `critical`, whose red is dark enough to keep the default light ink.
// The badge hugs its label at a fixed height, down to a 60px floor — the 68px
// minimum column width less the 4px padding the column and the badge each carry.
const badgeFill: Record<FlightTableBadgeStatus, string> = {
    critical: 'bg-status-critical',
    warning: 'bg-status-warning',
    success: 'bg-status-success',
    info: 'bg-status-info',
    neutral: 'bg-status-neutral',
}

/**
 * The content rendered inside a {@link TableCell} (Figma node 7721:128857 —
 * `type=text|time(sta)|baggage|naflt`, plus the `TextTooltip` popover body).
 * Token-driven presentational primitive: `text` (P1 value), `time` (time + a
 * warning-toned date line), `baggage` (suitcase glyph + teal delivery time),
 * `naflt` (compact nature-of-flight badge), `badge` (status-filled public-remark
 * pill) and `tooltip` (an elevation popover body). Dynamic, data-driven colours
 * (nature-of-flight hue, baggage delivery state) are passed in as classes by the
 * live cell. (AOS-630)
 */
export const FlightTableCellContent: FC<FlightTableCellContentProps> = props => {
    switch (props.type) {
        case 'text':
            return (
                <Text
                    size='body-lg'
                    tone={props.tone ?? 'default'}
                    className={twMerge('text-center', props.className)}
                    testID={props.testID}
                >
                    {props.value}
                </Text>
            )

        case 'link':
            return (
                <Box
                    className={twMerge('flex-row items-center gap-1', props.className)}
                    testID={props.testID}
                >
                    <Text size='body-lg' className='text-center text-content-link underline'>
                        {props.value}
                    </Text>
                    {props.checked === true && (
                        <CheckCircleGlyph size={16} color={GLYPH_COLORS.link} />
                    )}
                </Box>
            )

        case 'time':
            return (
                <Box className={twMerge('items-center', props.className)} testID={props.testID}>
                    <Text size='body-lg' weight='medium'>
                        {props.time}
                    </Text>
                    {props.date !== undefined && props.date !== null && (
                        <Text size='caption' weight='medium' tone={props.dateTone ?? 'warning'}>
                            {props.date}
                        </Text>
                    )}
                </Box>
            )

        case 'baggage':
            return (
                <Box
                    className={twMerge('items-center justify-center', props.className)}
                    testID={props.testID}
                >
                    <BaggageGlyph size={24} color={props.iconColor ?? GLYPH_COLORS.default} />
                    {props.time !== undefined && props.time !== null && (
                        <Text
                            size='body'
                            weight='medium'
                            className={twMerge(
                                'text-center text-graphics-teal',
                                props.timeClassName,
                            )}
                        >
                            {props.time}
                        </Text>
                    )}
                </Box>
            )

        case 'naflt':
            return (
                <Box
                    className={twMerge(
                        'h-5 min-w-12 items-center justify-center rounded-sm px-1',
                        props.colorClassName ?? 'bg-graphics-grey',
                        props.className,
                    )}
                    testID={props.testID}
                >
                    <Text size='caption' weight='semiBold' tone='on-graphics'>
                        {props.label}
                    </Text>
                </Box>
            )

        case 'badge':
            return (
                <Box
                    className={twMerge(
                        'min-w-[60px] max-w-full shrink items-center justify-center rounded px-1',
                        badgeFill[props.status],
                        props.className,
                    )}
                    testID={props.testID}
                >
                    <Text
                        size='caption'
                        weight='semiBold'
                        numberOfLines={2}
                        className='w-full text-center [overflow-wrap:anywhere]'
                        tone={props.status === 'critical' ? 'default' : 'on-graphics'}
                    >
                        {props.label}
                    </Text>
                </Box>
            )

        case 'tooltip':
            return (
                <Box
                    className={twMerge(
                        'items-start gap-0 rounded-sm bg-background-elevation p-2',
                        props.className,
                    )}
                    testID={props.testID}
                >
                    {props.children}
                </Box>
            )
    }
}

FlightTableCellContent.displayName = 'FlightTableCellContent'
